import axios from 'axios'
import { useEventListener } from '@vueuse/core'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import useBrandAppConfig from './useBrandAppConfig'
import useContentEditing from './useContentEditing'
import useDirtySnapshot from './useDirtySnapshot'
import useBrandAppHome from './brandAppHome'
import { resetMissionInstances } from './missionInstances'
import { EDITOR_TEXTS_SAVED } from './editorTexts'
import {
  EDITOR_LOYALTY_DATA,
  type AudiencePreviewRule,
  type CustomerStatusState,
  type PointsExclusionState
} from './editorLoyaltyData'
import useWebappMissions from '@/composables/webappMissions'
import useStore from '@/composables/store'
import { getApiErrorMessage } from '@/utils/apiError'

import {
  BRAND_APP_TABS,
  completionFieldsFromApi,
  loyaltyBlocksFromApi,
  type BrandAppShowcaseStore,
  type BrandAppTabId
} from '../lib/brandAppContract'
import { COMING_SOON_SCREENS } from '../lib/brandAppSections'
import { withStoreAddress } from '../lib/brandAppStores'
import { isInvalidUrlInput } from '../lib/brandAppUrl'
import {
  hasInvalidSocials,
  socialsFromApi,
  socialsPayload,
  type WebappHomeFields,
  type WebappSocials
} from '../lib/brandAppHomeApi'
import { fontFromApi, fontInput, fontKey, type FontRole, type WebappFont } from '../lib/brandAppFonts'
import type { Audience, ImageFile, LoyaltyProgram, LoyaltyReward, MerchantTexts } from '@/types/types'

const PREVIEW_REWARDS_PAGE_SIZE = 100

export default function useWebappEditorData() {
  const notification = useNotification()
  const { t } = useI18n()
  const router = useRouter()
  const route = useRoute()
  const brandAppConfig = useBrandAppConfig()
  const brandAppHome = useBrandAppHome()
  const { stopEditing } = useContentEditing()
  const { getMerchantConfigs, merchantConfigs } = useMerchantConfigs()
  const { merchantMediaForm, getMerchantMedias, updateMerchantFormMedias } = useMerchantMedias()
  const {
    fillWebappRegistrationTexts,
    getMerchantTexts,
    patchWebappTexts,
    merchantTextsForm,
    isPolite,
    webappTextDefaults
  } = useMerchantTexts()
  const { fetchLoyaltyProgramRewardsPaged } = useReward()
  const { getStoresByModule } = useStore()
  const { saveWebapp, getWebapp } = useWebapp()
  const { user, merchantLogo, stores } = useLocalStorage()
  const { fetchMerchantAudiences } = useAudience()
  const { missions, isLoadingMissions, missionsError, getMissions, saveMissions } = useWebappMissions()

  watch(
    isPolite,
    (value) => {
      brandAppConfig.updateIdentity('usePoliteLanguage', value)
    },
    { immediate: true }
  )

  const merchantTexts = ref<MerchantTexts>(null)
  const areMerchantTextsLoaded = ref(false)
  const deletedImages = ref<number[]>([])
  const isLoading = ref(true)
  const isSaving = ref(false)
  const isWebappLoaded = ref(false)

  const firstStoreName = computed(() => {
    if (stores.value.length > 0) {
      return stores.value[0].name
    }

    return 'Store'
  })
  const pageTitle = computed(() => ({
    title: t('pages.loyaltySupports.webapp.title')
  }))

  const emptyLoyaltyProgram: LoyaltyProgram = {
    cguUrl: '',
    cguWording: '',
    id: 0,
    privacyPolicyUrl: ''
  }
  const loyaltyProgram = ref<LoyaltyProgram>({ ...emptyLoyaltyProgram })
  const initialLoyaltyProgram = ref<LoyaltyProgram>({ ...emptyLoyaltyProgram })
  const getLoyaltyProgram = async () => {
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/loyalty-program`)
      loyaltyProgram.value = {
        cguUrl: data.data.cguUrl ?? '',
        cguWording: data.data.cguWording ?? '',
        id: data.data.id,
        privacyPolicyUrl: data.data.privacyPolicyUrl ?? ''
      }
      initialLoyaltyProgram.value = { ...loyaltyProgram.value }
    } catch {
      notification.create('error', {
        text: t('pages.loyaltyPage.tabs.settings.errors.cguLinkUpdate'),
        persist: true,
        timeout: 4000
      })
    }
  }

  const loyaltyProgramPayload = computed(() => {
    const payload: Record<string, string> = {}
    if (loyaltyProgram.value.cguUrl !== initialLoyaltyProgram.value.cguUrl) {
      payload.cgu_url = loyaltyProgram.value.cguUrl.trim()
    }
    if (loyaltyProgram.value.privacyPolicyUrl !== initialLoyaltyProgram.value.privacyPolicyUrl) {
      payload.privacy_policy_url = loyaltyProgram.value.privacyPolicyUrl.trim()
    }
    if (loyaltyProgram.value.cguWording !== initialLoyaltyProgram.value.cguWording) {
      payload.cgu_wording = loyaltyProgram.value.cguWording
    }

    return payload
  })

  const hasInvalidUrls = computed(
    () =>
      isInvalidUrlInput(loyaltyProgram.value.cguUrl) ||
      isInvalidUrlInput(loyaltyProgram.value.privacyPolicyUrl) ||
      isInvalidUrlInput(brandAppConfig.order.value.catalogUrl) ||
      Object.values(brandAppConfig.home.value.menuStoreUrls).some(
        (url) => url.trim() === '' || isInvalidUrlInput(url)
      ) ||
      hasInvalidSocials(brandAppConfig.socials.value)
  )

  const updateLoyaltyProgram = async () => {
    await axios.patch(
      `/merchants/${user.value.merchant.id}/loyalty-program/${loyaltyProgram.value.id}`,
      loyaltyProgramPayload.value
    )
    initialLoyaltyProgram.value = { ...loyaltyProgram.value }
  }

  const webappImages = ref<ImageFile[]>([])
  const webapp = ref<
    {
      color: string
      hasFormCompletion: boolean
      hasCustomerStatus?: boolean
      statusSegmentIds?: number[]
      id: number
      images: ImageFile[]
      link: string
      buttonColor?: string | null
      textColor?: string | null
      tabs?: string[] | null
      socials?: WebappSocials | null
      completionFields?: string[] | null
      loyaltyBlocks?: string[] | null
    } & WebappHomeFields
  >({
    color: '#000000',
    hasFormCompletion: false,
    id: 0,
    images: [],
    link: ''
  })
  const loadBrandAppHome = async () => {
    if (!isWebappLoaded.value) return
    try {
      await brandAppHome.load(webapp.value.id, webapp.value)
    } catch (error) {
      console.error(error)
    }
  }

  const FONT_ROLES: FontRole[] = ['heading', 'body']

  const fontOf = (role: FontRole) =>
    role === 'heading' ? brandAppConfig.identity.value.fontHeading : brandAppConfig.identity.value.fontBody

  const loadFonts = async () => {
    if (!isWebappLoaded.value) return
    try {
      const { data } = await axios.get<{ data: { heading: WebappFont | null; body: WebappFont | null } }>(
        `/webapps/${webapp.value.id}/fonts`
      )
      brandAppConfig.updateIdentity('fontHeading', fontFromApi(data.data?.heading))
      brandAppConfig.updateIdentity('fontBody', fontFromApi(data.data?.body))
    } catch (error) {
      console.error(error)
    }
  }

  const saveFonts = async () => {
    const changedRoles = FONT_ROLES.filter((role) => fontSnapshots[role].isDirty.value)
    if (changedRoles.length === 0) return

    await axios.put(
      `/webapps/${webapp.value.id}/fonts`,
      Object.fromEntries(changedRoles.map((role) => [role, fontInput(fontOf(role))]))
    )
    changedRoles.forEach((role) => fontSnapshots[role].capture())
  }

  const getWebappData = async () => {
    try {
      const dataWebapp = await getWebapp()
      if (dataWebapp) {
        webapp.value = dataWebapp
        isWebappLoaded.value = true
      }
    } catch (error) {
      console.error(error)
    }
  }

  const toneAlignedTextKeys = ref<string[]>([])

  const alignWelcomeTextsWithTone = (isFormal: boolean) => {
    const current = webappTextDefaults(isFormal)
    const other = webappTextDefaults(!isFormal)
    const fields = merchantTextsForm.value.webappTextsFields
    const keys = Object.keys(current) as (keyof typeof current)[]

    toneAlignedTextKeys.value = keys.filter((key) => {
      if (other[key] === current[key] || fields[key].value?.trim() !== other[key]) return false
      fields[key].value = current[key]
      return true
    })
  }

  const loadMerchantTexts = async () => {
    const data = await getMerchantTexts()
    if (!data) return

    merchantTextsForm.value.webappTextsFields.webappHomeTitle.value = data.webappHomeTitle
    merchantTextsForm.value.webappTextsFields.webappHomeSubtitle.value = data.webappHomeSubtitle
    merchantTextsForm.value.languageTone.usePoliteLanguage.value = data.usePoliteLanguage
    alignWelcomeTextsWithTone(data.usePoliteLanguage)
    fillWebappRegistrationTexts(data)
    merchantTexts.value = data
    areMerchantTextsLoaded.value = true
  }

  const customerStatus = ref<CustomerStatusState>({ isEnabled: false, audienceIds: [] })
  const pointsExclusion = ref<PointsExclusionState>({ isEnabled: false, audienceIds: [] })
  const audiences = ref<Audience[]>([])
  const audiencePreviewRule = ref<AudiencePreviewRule | null>(null)
  const getAudiences = async () => {
    try {
      const response = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 } })
      audiences.value = response?.data ?? []
    } catch {
      audiences.value = []
    }
  }

  const reloadMissions = async () => {
    if (!isWebappLoaded.value) return
    await getMissions(webapp.value.id)
    missionsSnapshot.capture()
  }

  const rewards = ref<LoyaltyReward[]>([])
  const getRewards = async () => {
    const { data } = await fetchLoyaltyProgramRewardsPaged(undefined, 'points', 'ASC', 1, PREVIEW_REWARDS_PAGE_SIZE)
    rewards.value = data
  }

  const loyaltyStores = ref<BrandAppShowcaseStore[]>([])
  const getPublicStoreIds = async (): Promise<Set<number> | null> => {
    try {
      const { data } = await axios.get<{ data?: { id: number }[] }>(
        `/public/merchants/${user.value.merchant.slug}/stores`
      )

      return new Set((data?.data ?? []).map((store) => store.id))
    } catch {
      return null
    }
  }

  const getLoyaltyStores = async () => {
    const [moduleStores, publicIds] = await Promise.all([
      getStoresByModule('loyalty') as Promise<BrandAppShowcaseStore[]>,
      getPublicStoreIds()
    ])
    const stores = publicIds ? moduleStores.filter((store) => publicIds.has(store.id)) : moduleStores
    loyaltyStores.value = stores
    const details = await Promise.allSettled(stores.map((store) => axios.get(`/stores/${store.id}`)))
    loyaltyStores.value = stores.map((store, index) => {
      const detail = details[index]

      return detail.status === 'fulfilled' ? withStoreAddress(store, detail.value.data?.data?.storeAddress) : store
    })
  }

  const deleteImages = async () => {
    for (const id of [...deletedImages.value]) {
      await axios.delete(`/webapp-images/${id}`)
      deletedImages.value = deletedImages.value.filter((imageId) => imageId !== id)
    }
  }

  const saveLogo = async () => {
    merchantMediaForm.value.logo.pictureUrl = brandAppConfig.identity.value.logo
    await updateMerchantFormMedias({ rethrow: true })
    const medias = await getMerchantMedias()
    merchantLogo.value = medias?.logo || brandAppConfig.identity.value.logo
    brandAppConfig.updateIdentity('logo', merchantLogo.value)
  }

  const welcomeTexts = computed(() => {
    const defaults = webappTextDefaults(brandAppConfig.identity.value.usePoliteLanguage)
    const fields = merchantTextsForm.value?.webappTextsFields

    return {
      title: fields?.webappHomeTitle.value?.trim() || defaults.webappHomeTitle,
      phoneTitle: fields?.webappHomeSubtitle.value?.trim() || defaults.webappHomeSubtitle
    }
  })

  const fillEmptyWelcomeTexts = () => {
    const fields = merchantTextsForm.value.webappTextsFields
    if (!fields.webappHomeTitle.value?.trim()) fields.webappHomeTitle.value = welcomeTexts.value.title
    if (!fields.webappHomeSubtitle.value?.trim()) fields.webappHomeSubtitle.value = welcomeTexts.value.phoneTitle
  }

  const submitWebapp = async () => {
    if (hasInvalidUrls.value) return

    try {
      isSaving.value = true
      if (!isWebappLoaded.value) throw new Error('brand-app-not-loaded')

      if (deletedImages.value.length > 0) await deleteImages()
      if (Object.keys(loyaltyProgramPayload.value).length > 0) await updateLoyaltyProgram()
      if (areMerchantTextsLoaded.value) fillEmptyWelcomeTexts()
      const textKeys = [...new Set([...changedTextKeys.value, ...toneAlignedTextKeys.value])]
      if (areMerchantTextsLoaded.value && textKeys.length > 0) {
        await patchWebappTexts(textKeys)
        toneAlignedTextKeys.value = []
        captureTexts()
      }
      if (brandAppConfig.identity.value.logo !== merchantLogo.value) await saveLogo()
      otherSnapshot.capture()

      const payload = {
        id: webapp.value.id,
        color: brandAppConfig.identity.value.primary,
        hasFormCompletion: webapp.value.hasFormCompletion,
        hasCustomerStatus: customerStatus.value.isEnabled,
        statusSegmentIds: customerStatus.value.isEnabled ? customerStatus.value.audienceIds : []
      }

      if (
        webappSnapshot.isDirty.value ||
        brandAppHome.areFieldsDirty.value ||
        colorsSnapshot.isDirty.value ||
        tabsSnapshot.isDirty.value ||
        socialsSnapshot.isDirty.value ||
        completionSnapshot.isDirty.value ||
        loyaltyBlocksSnapshot.isDirty.value
      ) {
        const home = brandAppHome.areFieldsDirty.value ? brandAppHome.fieldsPayload() : undefined
        const colors = colorsSnapshot.isDirty.value ? colorsState() : undefined
        const tabs = tabsSnapshot.isDirty.value ? tabsState() : undefined
        const socials = socialsSnapshot.isDirty.value ? socialsState() : undefined
        const completionFields = completionSnapshot.isDirty.value ? completionState() : undefined
        const loyaltyBlocks = loyaltyBlocksSnapshot.isDirty.value ? loyaltyBlocksState() : undefined
        if (webappImages.value.length === 0) {
          await saveWebapp({ ...payload, home, colors, tabs, socials, completionFields, loyaltyBlocks })
        } else {
          for (const [index, image] of webappImages.value.entries()) {
            const isFirst = index === 0
            await saveWebapp({
              ...payload,
              image,
              home: isFirst ? home : undefined,
              colors: isFirst ? colors : undefined,
              tabs: isFirst ? tabs : undefined,
              socials: isFirst ? socials : undefined,
              completionFields: isFirst ? completionFields : undefined,
              loyaltyBlocks: isFirst ? loyaltyBlocks : undefined
            })
          }
        }
      }

      webappImages.value = []
      webappSnapshot.capture()
      colorsSnapshot.capture()
      tabsSnapshot.capture()
      socialsSnapshot.capture()
      completionSnapshot.capture()
      loyaltyBlocksSnapshot.capture()
      brandAppHome.captureFields()

      if (missionsSnapshot.isDirty.value && !missionsError.value && missions.value.length > 0) {
        await saveMissions(webapp.value.id)
        missionsSnapshot.capture()
      }

      await brandAppHome.save(webapp.value.id)
      await saveFonts()
      await getWebappData()
      await loadBrandAppHome()
      await loadFonts()
      capture()
      notification.create('success', {
        text: t('pages.loyaltySupports.webapp.notifications.save.success'),
        timeout: 3000
      })
    } catch (error) {
      notification.create('error', {
        text: t('pages.loyaltySupports.webapp.notifications.save.error', {
          error: getApiErrorMessage(error) ?? t('pages.loyaltySupports.webapp.notifications.save.errorGeneric')
        }),
        persist: true
      })
    } finally {
      isSaving.value = false
    }
  }

  const webappURL = computed(() => {
    const brandAppUrl = import.meta.env.VITE_BRAND_APP_URL
    if (brandAppUrl) return brandAppUrl

    return import.meta.env.VITE_ENV === 'local'
      ? `http://localhost:3004/${user.value.merchant.slug}`
      : webapp.value.link
  })

  onMounted(async () => {
    isLoading.value = true
    stopEditing()
    resetMissionInstances()
    try {
      await Promise.allSettled([
        loadMerchantTexts(),
        getMerchantConfigs(),
        getLoyaltyProgram(),
        getWebappData(),
        getRewards(),
        getLoyaltyStores(),
        getAudiences()
      ])
      if (isWebappLoaded.value) {
        customerStatus.value = {
          isEnabled: webapp.value.hasCustomerStatus ?? false,
          audienceIds: webapp.value.statusSegmentIds ?? []
        }
        await getMissions(webapp.value.id)
      }
      brandAppConfig.seed({
        brandName: user.value.merchant?.name ?? '',
        logo: merchantLogo.value,
        primary: webapp.value.color,
        buttonColor: webapp.value.buttonColor ?? undefined,
        usePoliteLanguage: isPolite.value
      })
      seedTabs(webapp.value.tabs)
      brandAppConfig.socials.value = socialsFromApi(webapp.value.socials)
      brandAppConfig.setCompletionFields(completionFieldsFromApi(webapp.value.completionFields))
      brandAppConfig.setLoyaltyBlocks(loyaltyBlocksFromApi(webapp.value.loyaltyBlocks))
      await loadBrandAppHome()
      brandAppConfig.updateHome('dressing', {
        ...brandAppConfig.home.value.dressing,
        homeTextColor: webapp.value.textColor ?? undefined
      })
      await loadFonts()
    } finally {
      capture()
      isLoading.value = false
    }
  })

  onBeforeUnmount(() => stopEditing())

  const renderImages = computed(() => {
    if (deletedImages.value.length > 0) {
      return [...webappImages.value, ...webapp.value.images].filter((image) => !deletedImages.value.includes(image.id))
    }

    return [...webappImages.value, ...webapp.value.images]
  })

  const textValues = (fields: Record<string, { value: string }> | undefined) =>
    Object.fromEntries(Object.entries(fields ?? {}).map(([key, field]) => [key, field.value]))

  const webappState = () => ({
    color: brandAppConfig.identity.value.primary,
    hasFormCompletion: webapp.value.hasFormCompletion,
    imageCount: webappImages.value.length,
    customerStatus: customerStatus.value
  })

  const colorsState = () => ({
    buttonColor: brandAppConfig.identity.value.buttonColor ?? null,
    textColor: brandAppConfig.home.value.dressing.homeTextColor ?? null
  })

  const tabsState = () => brandAppConfig.tabs.value.filter((tab) => !COMING_SOON_SCREENS.includes(tab))

  const socialsState = () => socialsPayload(brandAppConfig.socials.value)

  const completionState = () => [...brandAppConfig.completionFields.value]

  const loyaltyBlocksState = () => [...brandAppConfig.loyaltyBlocks.value]

  const seedTabs = (saved: string[] | null | undefined) => {
    const known = BRAND_APP_TABS.map((tab) => tab.id)
    const tabs = (saved ?? []).filter((tab): tab is BrandAppTabId => known.includes(tab as BrandAppTabId))
    if (tabs.length === 0) return
    brandAppConfig.setTabs(tabs)
    brandAppConfig.setTabOrder([...tabs, ...known.filter((tab) => !tabs.includes(tab))])
  }

  const otherState = () => ({
    logo: brandAppConfig.identity.value.logo,
    deletedImages: deletedImages.value,
    loyaltyProgram: loyaltyProgram.value
  })

  const textsState = (): Record<string, string> => ({
    ...textValues(merchantTextsForm.value?.webappTextsFields),
    ...textValues(merchantTextsForm.value?.webappRegistrationTextsFields)
  })

  const savedTexts = ref<Record<string, string> | null>(null)
  const changedTextKeys = computed(() => {
    const saved = savedTexts.value
    if (!saved) return []

    return Object.entries(textsState())
      .filter(([key, value]) => saved[key] !== value)
      .map(([key]) => key)
  })
  const captureTexts = () => {
    savedTexts.value = textsState()
  }

  const webappSnapshot = useDirtySnapshot(webappState)
  const missionsSnapshot = useDirtySnapshot(() => missions.value)
  const otherSnapshot = useDirtySnapshot(otherState)
  const colorsSnapshot = useDirtySnapshot(colorsState)
  const tabsSnapshot = useDirtySnapshot(tabsState)
  const socialsSnapshot = useDirtySnapshot(socialsState)
  const completionSnapshot = useDirtySnapshot(completionState)
  const loyaltyBlocksSnapshot = useDirtySnapshot(loyaltyBlocksState)
  const fontSnapshots: Record<FontRole, ReturnType<typeof useDirtySnapshot>> = {
    heading: useDirtySnapshot(() => fontKey(fontOf('heading'))),
    body: useDirtySnapshot(() => fontKey(fontOf('body')))
  }
  const snapshots = [
    webappSnapshot,
    missionsSnapshot,
    otherSnapshot,
    colorsSnapshot,
    tabsSnapshot,
    socialsSnapshot,
    completionSnapshot,
    loyaltyBlocksSnapshot,
    ...Object.values(fontSnapshots)
  ]
  const isDirty = computed(
    () =>
      changedTextKeys.value.length > 0 ||
      brandAppHome.isDirty.value ||
      snapshots.some((snapshot) => snapshot.isDirty.value)
  )
  const capture = () => {
    snapshots.forEach((snapshot) => snapshot.capture())
    brandAppHome.capture()
    captureTexts()
  }

  provide(EDITOR_TEXTS_SAVED, captureTexts)

  provide(EDITOR_LOYALTY_DATA, {
    missions,
    isLoadingMissions,
    missionsError,
    reloadMissions,
    customerStatus,
    pointsExclusion,
    audiencePreviewRule,
    audiences,
    primaryColor: computed(() => brandAppConfig.identity.value.primary),
    pointsLabel: computed(() => merchantTexts.value?.pointsLabel ?? 'points'),
    hasFormCompletion: computed({
      get: () => webapp.value.hasFormCompletion,
      set: (value: boolean) => {
        webapp.value = { ...webapp.value, hasFormCompletion: value }
      }
    })
  })

  useEventListener(window, 'beforeunload', (event: BeforeUnloadEvent) => {
    if (!isDirty.value) return
    event.preventDefault()
  })

  const isUnsavedChangesModalOpen = ref(false)
  const pendingNavigation = ref<string | null>(null)
  const isLeavingAfterConfirm = ref(false)

  onBeforeRouteLeave((to) => {
    if (to.path === route.path) return true
    if (isLeavingAfterConfirm.value || !isDirty.value || isLoading.value) return true

    pendingNavigation.value = to.fullPath
    isUnsavedChangesModalOpen.value = true

    return false
  })

  const confirmLeave = () => {
    isUnsavedChangesModalOpen.value = false
    isLeavingAfterConfirm.value = true
    if (pendingNavigation.value) router.push(pendingNavigation.value)
  }

  const cancelLeave = () => {
    isUnsavedChangesModalOpen.value = false
    pendingNavigation.value = null
  }

  const leaveEditor = () => {
    router.push({ name: 'LoyaltyEdit' })
  }

  return {
    brandAppConfig,
    cancelLeave,
    audiencePreviewRule,
    audiences,
    confirmLeave,
    customerStatus,
    pointsExclusion,
    deletedImages,
    firstStoreName,
    hasInvalidUrls,
    isDirty,
    isLoading,
    isSaving,
    isUnsavedChangesModalOpen,
    leaveEditor,
    loyaltyProgram,
    loyaltyStores,
    merchantConfigs,
    merchantLogo,
    merchantTexts,
    merchantTextsForm,
    pageTitle,
    renderImages,
    rewards,
    submitWebapp,
    user,
    webapp,
    webappImages,
    webappURL,
    welcomeTexts
  }
}
